<script lang="ts" setup>
  import moment from 'moment';
  import { ref, computed, watch } from 'vue';
  import { ClickOutside } from 'vuetify/directives/click-outside';

  import { BvMenu, BvBtn } from '@bv/components';
  import BvCalendar from '@bv/components/form/inputs/date/BvCalendar.vue';
  import DateIcon from '@bv/components/form/inputs/date/partials/DateIcon.vue';
  import type {
    DateInputProps,
    DateRange,
    DateValue,
    CustomDateRange,
    CustomDateRangeWithId,
  } from '@bv/components/form/inputs/date/partials/DateInput.type';
  import BvInputLabel from '@bv/components/form/inputs/partials/BvInputLabel.vue';
  import { trans } from '@bv/utils';

  defineOptions({
    directives: {
      ClickOutside,
    },
  });

  // Use defineModel for v-model binding
  // Output format: [date] for single, [startDate, endDate] for range
  const modelValue = defineModel<DateValue>({ default: null });

  const props = withDefaults(defineProps<DateInputProps>(), {
    size: 'md',
    required: false,
    error: false,
    disabled: false,
    withRanges: false,
    customRanges: () => [],
    clearText: () => trans('global.form.clear_trans'),
    doneText: () => trans('global.form.done_trans'),
    color: 'base',
    type: 'single',
    hideActions: false,
  });

  const dates = ref<string[] | string>([]);
  const menu = ref(false);

  // Temporary value for BvCalendar (doesn't update modelValue until Done is clicked)
  // BvCalendar expects: string | [startDate, endDate] | null
  const tempCalendarValue = ref<string | [string, string] | null>(null);

  // ==========================================
  // COMPUTED PROPERTIES
  // ==========================================
  const customRanges = computed<CustomDateRangeWithId[]>(() => {
    return props.customRanges.map((range: CustomDateRange, i) => ({ ...range, id: i }));
  });

  // Display the selected date range in the input field
  const dateRangeText = computed<string | null>(() => {
    // Handle the case where dates.value could be a string (single date) or array (multiple dates)
    const datesArray = Array.isArray(dates.value) ? dates.value : dates.value ? [dates.value] : [];

    if (!datesArray || datesArray.length === 0) {
      return null;
    }

    if (datesArray.length === 1) {
      return formatDateForDisplay(datesArray[0]);
    }

    // For ranges, show start and end dates
    const sortedDates = [...datesArray].sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
    const start = formatDateForDisplay(sortedDates[0]);
    const end = formatDateForDisplay(sortedDates[sortedDates.length - 1]);

    if (start === end) {
      return start;
    }

    return `${start} - ${end}`;
  });

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  /**
   * Convert a DateValue (string or [start, end] array) to a DateRange object for internal use
   */
  const convertToDateRange = (value: DateValue | string | [string, string] | null): DateRange | null => {
    if (!value) {
      return null;
    }

    // Handle string format (single date)
    if (typeof value === 'string') {
      return {
        start: value,
        end: value,
      };
    }

    // Handle array format: [startDate, endDate] for range
    return {
      start: value[0],
      end: value[1],
    };
  };

  /**
   * Format date for display in the input field
   */
  const formatDateForDisplay = (date: string): string | null => {
    if (!date) {
      return null;
    }
    return moment(date).format('D/M/YYYY');
  };

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Convert DateValue to BvCalendar format (string | [startDate, endDate] | null)
   */
  const convertToCalendarValue = (value: DateValue): string | [string, string] | null => {
    if (!value) {
      return null;
    }
    // Pass through - both use the same format
    return value;
  };

  /**
   * Convert BvCalendar format to DateValue
   */
  const convertFromCalendarValue = (value: string | [string, string] | null): DateValue => {
    return value;
  };

  /**
   * Sync the calendar draft and input display with the committed model value
   */
  const syncWithModelValue = (): void => {
    tempCalendarValue.value = convertToCalendarValue(modelValue.value);

    if (!modelValue.value) {
      dates.value = [];
      return;
    }

    const dateRange = convertToDateRange(modelValue.value);
    if (!dateRange) {
      dates.value = [];
      return;
    }

    const startDate = moment(dateRange.start);
    const endDate = moment(dateRange.end);

    if (props.type === 'single') {
      dates.value = startDate.format('YYYY-MM-DD');
      return;
    }

    const allDates: string[] = [];
    const currentDate = moment(startDate);
    while (currentDate.isSameOrBefore(endDate)) {
      allDates.push(currentDate.format('YYYY-MM-DD'));
      currentDate.add(1, 'day');
    }
    dates.value = allDates;
  };

  /**
   * Close Date picker dropdown and discard uncommitted changes when actions are shown
   */
  const closeDatePicker = (): void => {
    if (!props.hideActions) {
      syncWithModelValue();
    }

    if (menu.value) {
      menu.value = false;
    }
  };

  /**
   * Handle calendar update (from BvCalendar)
   */
  const handleCalendarUpdate = (): void => {
    if (props.disabled) {
      return;
    }

    // Convert tempCalendarValue to dates array for internal use (for display in input field)
    if (tempCalendarValue.value) {
      // tempCalendarValue is string for single, [startDate, endDate] for range
      if (typeof tempCalendarValue.value === 'string') {
        // Single date: string
        dates.value = moment(tempCalendarValue.value).format('YYYY-MM-DD');
      } else {
        // Range: [startDate, endDate] - generate all dates for display
        const startDate = moment(tempCalendarValue.value[0]);
        const endDate = moment(tempCalendarValue.value[1]);
        const allDates: string[] = [];
        const currentDate = moment(startDate);
        while (currentDate.isSameOrBefore(endDate)) {
          allDates.push(currentDate.format('YYYY-MM-DD'));
          currentDate.add(1, 'day');
        }
        dates.value = allDates;
      }
    } else {
      dates.value = [];
    }

    if (props.hideActions) {
      if (props.type === 'single') {
        applyDates();
      } else if (props.type === 'range') {
        if (Array.isArray(dates.value) && dates.value.length > 0) {
          applyDates();
        }
      }
    }
  };

  /**
   * Clear all selected dates
   */
  const clearDates = (): void => {
    dates.value = [];
    tempCalendarValue.value = null;
    modelValue.value = null;
    menu.value = false;
  };

  /**
   * Apply the selected dates (Done button)
   */
  const applyDates = (): void => {
    modelValue.value = convertFromCalendarValue(tempCalendarValue.value);
    menu.value = false;
  };

  /**
   * Watch for external modelValue changes
   */
  watch(() => modelValue.value, syncWithModelValue, { deep: true, immediate: true });
</script>

<template>
  <bv-menu
    v-model="menu"
    :close-on-content-click="false"
    :disabled="disabled"
    location="bottom"
    offset="4"
    transition="fade-transition"
    @update:model-value="!menu && closeDatePicker()"
  >
    <template v-slot:activator="{ props: menuProps }">
      <bv-input-label
        v-bind="disabled ? {} : menuProps"
        :disabled
        :error
        :error-messages
        :focused="menu"
        :label
        :required
        :size
      >
        <template #input>
          <input
            v-model="dateRangeText"
            class="flex min-w-0 flex-1 border-none bg-transparent text-sm outline-none"
            :class="{
              'cursor-pointer': !disabled,
              'cursor-not-allowed': disabled,
              'h-3.25': size === 'sm',
              'h-5.25': size === 'md',
            }"
            :disabled
            :placeholder
            readonly
          />
        </template>
        <template #append-icon>
          <date-icon />
        </template>
      </bv-input-label>
    </template>

    <!-- Date Picker Popup -->
    <div
      v-click-outside="closeDatePicker"
      class="w-fit overflow-y-auto rounded-2xl rounded-ss-none border border-gray-100 bg-white shadow-xl"
    >
      <bv-calendar
        v-model="tempCalendarValue"
        :bg-color
        :color
        :custom-ranges
        :disabled
        hide-header
        :max
        :min
        show-adjacent-months
        :type
        :with-ranges
        @update:model-value="handleCalendarUpdate"
      />
      <!-- Footer -->
      <div
        v-if="!hideActions"
        class="flex items-center justify-end gap-2 border-t border-gray-100 bg-white p-2"
      >
        <bv-btn
          class="h-fit p-2"
          color="gray"
          :text="clearText"
          variant="text"
          @click="clearDates"
        />
        <bv-btn
          class="h-fit p-2"
          color="base"
          :text="doneText"
          variant="text"
          @click="applyDates"
        />
      </div>
    </div>
  </bv-menu>
</template>
